/**
 * Pricing Configuration Component
 */
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  Home, Droplets, SquareStack, Sparkles, Navigation, Footprints, 
  Trees, Fence, Flame, Plus, Save, Loader2, Edit2, Trash2, DollarSign 
} from "lucide-react";
import { authAxios } from "@/utils/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_ICONS = {
  siding: Home,
  driveway: Droplets,
  windows: SquareStack,
  gutters: Navigation,
  entries: Footprints,
  sidewalk: Footprints,
  patio: Sparkles,
  deck: Trees,
  fence: Fence,
  roof: Home,
  chimney: Flame,
};

export default function PricingConfig() {
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [showAddService, setShowAddService] = useState(false);
  const [newService, setNewService] = useState({
    service_id: '',
    service_name: '',
    description: '',
    base_price: 0,
    price_per_sqft: 0,
    story_multiplier: 1,
    bundle_discount: 10,
    notes: ''
  });

  const fetchPricing = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/pricing`);
      setPricing(res.data);
    } catch {
      toast.error("Failed to load pricing");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPricing();
  }, [fetchPricing]);

  const updateServiceField = (serviceId, field, value) => {
    setPricing((prev) => ({
      ...prev,
      services: prev.services.map((s) =>
        s.service_id === serviceId ? { ...s, [field]: field === 'service_name' || field === 'description' || field === 'notes' ? value : (parseFloat(value) || 0) } : s
      ),
    }));
  };

  const deleteService = (serviceId) => {
    if (!window.confirm(`Are you sure you want to delete this service? This cannot be undone.`)) return;
    setPricing((prev) => ({
      ...prev,
      services: prev.services.filter((s) => s.service_id !== serviceId),
    }));
    toast.success("Service removed. Don't forget to save changes.");
  };

  const addService = () => {
    if (!newService.service_name || !newService.service_id) {
      toast.error("Service ID and Name are required");
      return;
    }
    if (pricing.services.some(s => s.service_id === newService.service_id)) {
      toast.error("Service ID already exists");
      return;
    }
    setPricing((prev) => ({
      ...prev,
      services: [...prev.services, { ...newService, tiers: [] }],
    }));
    setNewService({
      service_id: '',
      service_name: '',
      description: '',
      base_price: 0,
      price_per_sqft: 0,
      story_multiplier: 1,
      bundle_discount: 10,
      notes: ''
    });
    setShowAddService(false);
    toast.success("Service added. Don't forget to save changes.");
  };

  const savePricing = async () => {
    setSaving(true);
    try {
      await authAxios.put("/pricing", pricing);
      toast.success("Pricing saved successfully");
    } catch {
      toast.error("Failed to save pricing");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3, 4, 5].map((i) => (
          <Skeleton key={i} className="h-32 rounded-xl" />
        ))}
      </div>
    );
  }

  return (
    <div data-testid="pricing-config">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>
          Pricing Configuration
        </h1>
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={() => setShowAddService(true)}
            data-testid="add-service-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Service
          </Button>
          <Button
            onClick={savePricing}
            disabled={saving}
            className="btn-primary"
            data-testid="save-pricing-btn"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save Changes
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Add New Service Form */}
      {showAddService && (
        <Card className="border-blue-200 bg-blue-50/50 mb-6">
          <CardHeader>
            <CardTitle className="text-lg">Add New Service</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div className="space-y-2">
                <Label>Service ID *</Label>
                <Input
                  value={newService.service_id}
                  onChange={(e) => setNewService({ ...newService, service_id: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') })}
                  placeholder="e.g., pool_deck"
                  className="input-field"
                  data-testid="new-service-id"
                />
                <p className="text-xs text-slate-500">Lowercase, no spaces (used internally)</p>
              </div>
              <div className="space-y-2">
                <Label>Service Name *</Label>
                <Input
                  value={newService.service_name}
                  onChange={(e) => setNewService({ ...newService, service_name: e.target.value })}
                  placeholder="e.g., Pool Deck Cleaning"
                  className="input-field"
                  data-testid="new-service-name"
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label>Description</Label>
                <Input
                  value={newService.description}
                  onChange={(e) => setNewService({ ...newService, description: e.target.value })}
                  placeholder="e.g., Professional pool deck pressure washing"
                  className="input-field"
                  data-testid="new-service-description"
                />
              </div>
              <div className="space-y-2">
                <Label>Base Price ($)</Label>
                <Input
                  type="number"
                  value={newService.base_price}
                  onChange={(e) => setNewService({ ...newService, base_price: parseFloat(e.target.value) || 0 })}
                  className="input-field"
                  data-testid="new-service-price"
                />
              </div>
              <div className="space-y-2">
                <Label>Bundle Discount (%)</Label>
                <Input
                  type="number"
                  value={newService.bundle_discount}
                  onChange={(e) => setNewService({ ...newService, bundle_discount: parseFloat(e.target.value) || 0 })}
                  className="input-field"
                  data-testid="new-service-discount"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={addService} className="btn-primary" data-testid="confirm-add-service">
                Add Service
              </Button>
              <Button variant="outline" onClick={() => setShowAddService(false)}>
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="space-y-6">
        {pricing?.services.map((service) => {
          const Icon = SERVICE_ICONS[service.service_id] || Home;
          const isEditing = editingService === service.service_id;
          
          return (
            <Card key={service.service_id} className={`border-slate-200 ${isEditing ? 'ring-2 ring-blue-500' : ''}`} data-testid={`pricing-card-${service.service_id}`}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Icon className="w-5 h-5 text-blue-600" />
                    </div>
                    {isEditing ? (
                      <Input
                        value={service.service_name}
                        onChange={(e) => updateServiceField(service.service_id, "service_name", e.target.value)}
                        className="input-field max-w-xs font-semibold"
                        data-testid={`input-name-${service.service_id}`}
                      />
                    ) : (
                      service.service_name
                    )}
                  </CardTitle>
                  <div className="flex gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setEditingService(isEditing ? null : service.service_id)}
                      data-testid={`edit-service-${service.service_id}`}
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteService(service.service_id)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      data-testid={`delete-service-${service.service_id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                {isEditing && (
                  <div className="mt-3">
                    <Label className="text-sm text-slate-500">Description</Label>
                    <Input
                      value={service.description || ''}
                      onChange={(e) => updateServiceField(service.service_id, "description", e.target.value)}
                      placeholder="Service description..."
                      className="input-field mt-1"
                      data-testid={`input-desc-${service.service_id}`}
                    />
                  </div>
                )}
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <Label>Base Price ($)</Label>
                    <Input
                      type="number"
                      value={service.base_price}
                      onChange={(e) => updateServiceField(service.service_id, "base_price", e.target.value)}
                      className="input-field"
                      data-testid={`input-base-${service.service_id}`}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Price per Sq Ft ($)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={service.price_per_sqft}
                      onChange={(e) => updateServiceField(service.service_id, "price_per_sqft", e.target.value)}
                      className="input-field"
                      data-testid={`input-sqft-${service.service_id}`}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Story Multiplier</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={service.story_multiplier}
                      onChange={(e) => updateServiceField(service.service_id, "story_multiplier", e.target.value)}
                      className="input-field"
                      data-testid={`input-story-${service.service_id}`}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Bundle Discount (%)</Label>
                    <Input
                      type="number"
                      value={service.bundle_discount}
                      onChange={(e) => updateServiceField(service.service_id, "bundle_discount", e.target.value)}
                      className="input-field"
                      data-testid={`input-discount-${service.service_id}`}
                    />
                  </div>
                </div>
                {isEditing && (
                  <div className="mt-4">
                    <Label className="text-sm text-slate-500">Notes (shown to customers)</Label>
                    <Input
                      value={service.notes || ''}
                      onChange={(e) => updateServiceField(service.service_id, "notes", e.target.value)}
                      placeholder="e.g., Additional charges for stain removal"
                      className="input-field mt-1"
                      data-testid={`input-notes-${service.service_id}`}
                    />
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Card className="border-slate-200 mt-6">
        <CardHeader>
          <CardTitle>Quote Settings</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <DollarSign className="w-4 h-4 text-slate-400" />
                Minimum Estimate Amount ($)
              </Label>
              <Input
                type="number"
                min="0"
                step="5"
                value={pricing?.minimum_estimate || 175}
                onChange={(e) => setPricing({ ...pricing, minimum_estimate: parseFloat(e.target.value) || 0 })}
                className="input-field"
                data-testid="minimum-estimate-input"
              />
              <p className="text-xs text-slate-500">
                Customers cannot submit quotes below this amount. They'll see a warning message.
              </p>
            </div>
            
            <div className="space-y-2">
              <Label>Services needed for bundle discount</Label>
              <Select
                value={pricing?.bundle_threshold?.toString()}
                onValueChange={(val) => setPricing({ ...pricing, bundle_threshold: parseInt(val) })}
              >
                <SelectTrigger className="input-field" data-testid="bundle-threshold-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="2">2 Services</SelectItem>
                  <SelectItem value="3">3 Services</SelectItem>
                  <SelectItem value="4">4 Services</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
